<template>
    <div class="modal-bet-detail">
        <div class="bet-header">
            <div class="header-left">
                <div class="header-title">
                    <span class="game-name">{{ betGameName }}</span>
                    <a :href="betUrl" class="bet-id-link" @click.prevent="navigateToBet">
                        #{{ betId }}
                    </a>
                </div>
                <button class="copy-button" @click="copyBetId" title="Copy Bet ID">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="5" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/>
                        <path d="M5 5H3C2.44772 5 2 5.44772 2 6V13C2 13.5523 2.44772 14 3 14H10C10.5523 14 11 13.5523 11 13V11" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                </button>
            </div>
        </div>

        <div class="bet-content">
            <!-- User Information -->
            <div class="user-section">
                <div class="user-info">
                    <div class="user-avatar-wrapper">
                        <AvatarImage :image="bet.user ? bet.user.avatar : null" />
                    </div>
                    <div class="user-details">
                        <div class="user-name" v-if="bet.user" v-html="bet.user.username"></div>
                        <div class="user-name" v-else>Anonymous</div>
                        <div class="user-badge" v-if="bet.user">
                            <span class="badge-rank">{{ getUserRank }}</span>
                            <span class="badge-level">Level {{ bet.user.level || 0 }}</span>
                        </div>
                    </div>
                </div>
                <div class="bet-time">{{ formattedDate }}</div>
            </div>

            <!-- Game Icon -->
            <div class="game-icon-section">
                <div class="game-icon-wrapper" :class="'game-' + (bet.method || '')">
                    <img v-if="getGameImage" :src="getGameImage" :alt="betGameName" class="game-image" />
                    <IconDiamond v-else class="game-icon-fallback" />
                </div>
                <div class="game-name-text">{{ betGameName }}</div>
            </div>

            <!-- Bet Details -->
            <div class="bet-details">
                <div class="detail-row">
                    <span class="detail-label">Game:</span>
                    <span class="detail-value">{{ betGameName }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Multi:</span>
                    <span class="detail-value" :class="{ 'value-zero': betMultiplier === 0 }">
                        {{ (Number.isFinite(betMultiplier) ? betMultiplier : 0).toFixed(2) }}x
                    </span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Wager:</span>
                    <span class="detail-value">
                        <IconCoin />
                        {{ formatValue(betAmount) }}
                    </span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Rakeback:</span>
                    <span class="detail-value">
                        <IconCoin />
                        {{ formatValue(rakebackAmount) }}
                    </span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Payout:</span>
                    <span class="detail-value" :class="{ 'value-zero': bet.payout === 0 }">
                        <IconCoin />
                        {{ formatValue(bet.payout) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AvatarImage from '@/components/AvatarImage';
import IconCoin from '@/components/icons/IconCoin';
import IconDiamond from '@/components/icons/IconDiamond';

export default {
    name: 'ModalBetDetail',
    components: {
        AvatarImage,
        IconCoin,
        IconDiamond
    },
    methods: {
        ...mapActions(['notificationShow', 'modalsSetShow']),
        formatValue(value) {
            if (value == null || value === '') return '0.00';
            const num = parseFloat(value);
            return (Number.isFinite(num) ? num : 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        copyBetId() {
            // Get the current game type from modalsData or betDetail data
            const gameType = this.modalsData?.gameType || this.bet?.method?.toLowerCase() || 'crash';
            const betId = this.bet._id || this.betId;

            // Construct the full URL with hash parameters
            const baseUrl = window.location.origin + window.location.pathname;
            const fullUrl = `${baseUrl}#game=${gameType}&bet=${betId}`;

            const el = document.createElement('textarea');
            el.value = fullUrl;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.notificationShow({ type: 'success', message: 'Bet URL copied to clipboard.' });
        },
        navigateToBet() {
            const gameRoute = this.getGameRoute();
            const betId = this.bet._id || '';
            const gameName = this.bet.method || '';
            
            // Close modal first
            this.modalsSetShow(null);
            
            // Navigate to game page with hash using full URL
            setTimeout(() => {
                const fullUrl = `${window.location.protocol}//${window.location.host}${gameRoute}#game=${gameName}&bet=${betId}`;
                window.location.href = fullUrl;
            }, 100);
        },
        getGameRoute() {
            const routeMap = {
                'mines': '/mines',
                'crash': '/crash',
                'towers': '/towers',
                'blackjack': '/blackjack',
                'slots': '/slots',
                'roll': '/roll',
                'duels': '/duels',
                'unbox': '/unbox',
                'battles': '/battles'
            };
            const method = this.bet.method?.toLowerCase();
            if (routeMap[method]) return routeMap[method];
            if (this.bet.gameName) return '/slots';
            return '/';
        },
        getUserRank(user) {
            if (!user) return '';
            let rank = user.rakeback;
            if (user.rank !== 'user') {
                rank = user.rank;
            }
            return rank.toUpperCase();
        }
    },
    computed: {
        ...mapGetters(['modalsData']),
        bet() {
            return this.modalsData?.bet || {};
        },
        betId() {
            return this.bet._id ? this.bet._id.substring(0, 8) : 'unknown';
        },
        betUrl() {
            const gameRoute = this.getGameRoute();
            const betId = this.bet._id || '';
            const gameName = this.bet.method || '';
            return `${window.location.protocol}//${window.location.host}${gameRoute}#game=${gameName}&bet=${betId}`;
        },
        betGameName() {
            if (this.bet.gameName) return this.bet.gameName;
            if (this.bet.method === 'slots') return 'Slots';
            let method = this.bet.method ? this.bet.method.charAt(0).toUpperCase() + this.bet.method.slice(1) : '';
            if (this.bet.method === 'duels') method = 'Dice Duels';
            return method || 'Bet';
        },
        formattedDate() {
            if (!this.bet.updatedAt) return '';
            const date = new Date(this.bet.updatedAt);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        },
        betAmount() {
            let amount = this.bet.amount || 0;
            if (this.bet.method === 'blackjack') {
                amount = Math.floor((this.bet.amount?.main || 0) + (this.bet.amount?.sideLeft || 0) + (this.bet.amount?.sideRight || 0));
                if (this.bet.actions?.includes('split')) {
                    amount = Math.floor(amount + (this.bet.amount?.main || 0));
                }
            }
            return amount;
        },
        betMultiplier() {
            let multiplier = (this.bet.multiplier || 0) / 100;
            if (['crash', 'roll'].includes(this.bet.method)) {
                multiplier = (this.bet.payout || 0) / (this.betAmount || 1);
            }
            return multiplier;
        },
        rakebackAmount() {
            // Calculate rakeback (typically 1% of wager)
            return Math.floor(this.betAmount * 0.01 * 100) / 100;
        },
        getUserRank() {
            if (!this.bet.user) return '';
            let rank = this.bet.user.rakeback;
            if (this.bet.user.rank !== 'user') {
                rank = this.bet.user.rank;
            }
            return rank.toUpperCase();
        },
        getGameImage() {
            const imageMap = {
                'blackjack': require('@/assets/img/originals/blackjack.png'),
                'crash': require('@/assets/img/originals/crash.png'),
                'mines': require('@/assets/img/originals/mines.png'),
                'towers': require('@/assets/img/originals/tower.png'),
                'slots': require('@/assets/img/originals/slot.png'),
                'duels': require('@/assets/img/originals/duels.jpg')
            };
            const method = this.bet.method?.toLowerCase();
            return imageMap[method] || '';
        }
    }
}
</script>

<style scoped>
.modal-bet-detail {
    width: 500px;
    max-width: 90vw;
    background: var(--bg-purple-dark);
    border-radius: 15px;
    padding: 24px;
    color: #ffffff;
}

.bet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
}

.game-name {
    text-transform: capitalize;
}

.bet-id-link {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    font-size: 16px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;
}

.bet-id-link:hover {
    color: var(--accent-yellow);
    text-decoration: underline;
}

.copy-button {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s ease;
}

.copy-button:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
}

.bet-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.user-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.user-avatar-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--gradient-yellow);
    flex-shrink: 0;
}

.user-details {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.user-name {
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
}

.user-badge {
    display: flex;
    align-items: center;
    gap: 8px;
}

.badge-rank {
    padding: 4px 10px;
    background: rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    color: #60a5fa;
    text-transform: uppercase;
}

.badge-level {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
}

.bet-time {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    text-align: right;
}

.game-icon-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
}

.game-icon-wrapper {
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 8px;
    overflow: hidden;
}

.game-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
}

.game-icon-fallback {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
}

.game-name-text {
    font-size: 16px;
    font-weight: 600;
    text-transform: capitalize;
}

.bet-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
}

.detail-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
}

.detail-value {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    display: flex;
    align-items: center;
    gap: 6px;
}

.detail-value.value-zero {
    color: rgba(59, 130, 246, 0.8);
}

.detail-value svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

@media (max-width: 600px) {
    .modal-bet-detail {
        width: 95vw;
        padding: 20px;
    }

    .user-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .bet-time {
        text-align: left;
    }
}
</style>
